/**
 * IndexedDB Service for Client-Side Recording Chunks
 * Stores audio chunks temporarily until they're uploaded to S3
 */

const DB_NAME = 'velvet-meeting-recordings';
const DB_VERSION = 1;

const STORES = {
  CHUNKS: 'chunks',
  METADATA: 'upload_metadata'
};

/**
 * Initialize IndexedDB database
 * @returns {Promise<IDBDatabase>}
 */
export const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('IndexedDB error:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create chunks store
      if (!db.objectStoreNames.contains(STORES.CHUNKS)) {
        const chunksStore = db.createObjectStore(STORES.CHUNKS, {
          keyPath: 'id',
          autoIncrement: true
        });

        // Create indexes for efficient queries
        chunksStore.createIndex('meeting_id', 'meeting_id', { unique: false });
        chunksStore.createIndex('upload_status', 'upload_status', { unique: false });
        chunksStore.createIndex('timestamp', 'timestamp', { unique: false });
        chunksStore.createIndex('segment_number', 'segment_number', { unique: false });
      }

      // Create upload metadata store
      if (!db.objectStoreNames.contains(STORES.METADATA)) {
        db.createObjectStore(STORES.METADATA, { keyPath: 'meeting_id' });
      }

      console.log('IndexedDB initialized successfully');
    };
  });
};

/**
 * Save a chunk to IndexedDB
 * @param {Object} chunkData - Chunk data to save
 * @returns {Promise<number>} - Chunk ID
 */
export const saveChunk = async (chunkData) => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORES.CHUNKS, 'readwrite');
    const store = tx.objectStore(STORES.CHUNKS);

    return new Promise((resolve, reject) => {
      const request = store.add(chunkData);

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        console.error('Error saving chunk:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('saveChunk error:', error);
    throw error;
  }
};

/**
 * Get pending chunks for a meeting
 * @param {string} meetingId - Meeting ID
 * @returns {Promise<Array>} - Array of pending chunks
 */
export const getPendingChunks = async (meetingId) => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORES.CHUNKS, 'readonly');
    const store = tx.objectStore(STORES.CHUNKS);
    const index = store.index('upload_status');

    return new Promise((resolve, reject) => {
      const request = index.getAll('pending');

      request.onsuccess = () => {
        const chunks = request.result.filter(chunk => chunk.meeting_id === meetingId);
        resolve(chunks);
      };

      request.onerror = () => {
        console.error('Error getting pending chunks:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('getPendingChunks error:', error);
    throw error;
  }
};

/**
 * Get all chunks with a specific status
 * @param {string} status - Upload status ('pending', 'uploading', 'completed', 'failed')
 * @returns {Promise<Array>} - Array of chunks
 */
export const getChunksByStatus = async (status) => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORES.CHUNKS, 'readonly');
    const store = tx.objectStore(STORES.CHUNKS);
    const index = store.index('upload_status');

    return new Promise((resolve, reject) => {
      const request = index.getAll(status);

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        console.error('Error getting chunks by status:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('getChunksByStatus error:', error);
    throw error;
  }
};

/**
 * Update chunk status and metadata
 * @param {number} chunkId - Chunk ID
 * @param {string} status - New status
 * @param {Object} updates - Additional updates to apply
 * @returns {Promise<boolean>} - Success status
 */
export const updateChunkStatus = async (chunkId, status, updates = {}) => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORES.CHUNKS, 'readwrite');
    const store = tx.objectStore(STORES.CHUNKS);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(chunkId);

      getRequest.onsuccess = () => {
        const chunk = getRequest.result;

        if (!chunk) {
          reject(new Error(`Chunk ${chunkId} not found`));
          return;
        }

        const updatedChunk = {
          ...chunk,
          upload_status: status,
          ...updates
        };

        const putRequest = store.put(updatedChunk);

        putRequest.onsuccess = () => {
          resolve(true);
        };

        putRequest.onerror = () => {
          console.error('Error updating chunk:', putRequest.error);
          reject(putRequest.error);
        };
      };

      getRequest.onerror = () => {
        console.error('Error getting chunk:', getRequest.error);
        reject(getRequest.error);
      };
    });
  } catch (error) {
    console.error('updateChunkStatus error:', error);
    throw error;
  }
};

/**
 * Delete a chunk from IndexedDB
 * @param {number} chunkId - Chunk ID to delete
 * @returns {Promise<boolean>} - Success status
 */
export const deleteChunk = async (chunkId) => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORES.CHUNKS, 'readwrite');
    const store = tx.objectStore(STORES.CHUNKS);

    return new Promise((resolve, reject) => {
      const request = store.delete(chunkId);

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        console.error('Error deleting chunk:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('deleteChunk error:', error);
    throw error;
  }
};

/**
 * Get upload metadata for a meeting
 * @param {string} meetingId - Meeting ID
 * @returns {Promise<Object|null>} - Upload metadata
 */
export const getUploadMetadata = async (meetingId) => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORES.METADATA, 'readonly');
    const store = tx.objectStore(STORES.METADATA);

    return new Promise((resolve, reject) => {
      const request = store.get(meetingId);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        console.error('Error getting upload metadata:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('getUploadMetadata error:', error);
    throw error;
  }
};

/**
 * Update upload metadata for a meeting
 * @param {string} meetingId - Meeting ID
 * @param {Object} updates - Metadata updates
 * @returns {Promise<boolean>} - Success status
 */
export const updateUploadMetadata = async (meetingId, updates) => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORES.METADATA, 'readwrite');
    const store = tx.objectStore(STORES.METADATA);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(meetingId);

      getRequest.onsuccess = () => {
        const existing = getRequest.result || { meeting_id: meetingId };
        const updatedMetadata = {
          ...existing,
          ...updates,
          last_sync_at: Date.now()
        };

        const putRequest = store.put(updatedMetadata);

        putRequest.onsuccess = () => {
          resolve(true);
        };

        putRequest.onerror = () => {
          console.error('Error updating metadata:', putRequest.error);
          reject(putRequest.error);
        };
      };

      getRequest.onerror = () => {
        console.error('Error getting metadata:', getRequest.error);
        reject(getRequest.error);
      };
    });
  } catch (error) {
    console.error('updateUploadMetadata error:', error);
    throw error;
  }
};

/**
 * Get list of meetings with incomplete uploads
 * @returns {Promise<Array<string>>} - Array of meeting IDs
 */
export const getIncompleteUploads = async () => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORES.METADATA, 'readonly');
    const store = tx.objectStore(STORES.METADATA);

    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onsuccess = () => {
        const allMetadata = request.result;
        const incompleteMeetings = allMetadata
          .filter(meta => !meta.upload_complete)
          .map(meta => meta.meeting_id);
        resolve(incompleteMeetings);
      };

      request.onerror = () => {
        console.error('Error getting incomplete uploads:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('getIncompleteUploads error:', error);
    throw error;
  }
};

/**
 * Clean up completed meeting chunks
 * @param {string} meetingId - Meeting ID
 * @returns {Promise<number>} - Number of chunks deleted
 */
export const cleanupCompletedMeeting = async (meetingId) => {
  try {
    const db = await initDB();
    const tx = db.transaction([STORES.CHUNKS, STORES.METADATA], 'readwrite');
    const chunksStore = tx.objectStore(STORES.CHUNKS);
    const metadataStore = tx.objectStore(STORES.METADATA);

    return new Promise((resolve, reject) => {
      const index = chunksStore.index('meeting_id');
      const getAllRequest = index.getAll(meetingId);

      getAllRequest.onsuccess = () => {
        const chunks = getAllRequest.result;
        let deleteCount = 0;

        chunks.forEach(chunk => {
          chunksStore.delete(chunk.id);
          deleteCount++;
        });

        metadataStore.delete(meetingId);

        tx.oncomplete = () => {
          console.log(`Cleaned up ${deleteCount} chunks for meeting ${meetingId}`);
          resolve(deleteCount);
        };

        tx.onerror = () => {
          console.error('Error cleaning up meeting:', tx.error);
          reject(tx.error);
        };
      };

      getAllRequest.onerror = () => {
        console.error('Error getting chunks for cleanup:', getAllRequest.error);
        reject(getAllRequest.error);
      };
    });
  } catch (error) {
    console.error('cleanupCompletedMeeting error:', error);
    throw error;
  }
};

/**
 * Get database statistics
 * @returns {Promise<Object>} - Stats object
 */
export const getDBStats = async () => {
  try {
    const db = await initDB();
    const tx = db.transaction([STORES.CHUNKS, STORES.METADATA], 'readonly');
    const chunksStore = tx.objectStore(STORES.CHUNKS);
    const metadataStore = tx.objectStore(STORES.METADATA);

    return new Promise((resolve, reject) => {
      const chunksCount = chunksStore.count();
      const metadataCount = metadataStore.count();

      const results = {};

      chunksCount.onsuccess = () => {
        results.totalChunks = chunksCount.result;

        metadataCount.onsuccess = () => {
          results.totalMeetings = metadataCount.result;

          // Get status breakdown
          const statusIndex = chunksStore.index('upload_status');
          const statuses = ['pending', 'uploading', 'completed', 'failed'];
          const statusCounts = {};

          let completed = 0;

          statuses.forEach(status => {
            const countRequest = statusIndex.count(status);
            countRequest.onsuccess = () => {
              statusCounts[status] = countRequest.result;
              completed++;

              if (completed === statuses.length) {
                results.byStatus = statusCounts;
                resolve(results);
              }
            };
          });
        };

        metadataCount.onerror = () => {
          reject(metadataCount.error);
        };
      };

      chunksCount.onerror = () => {
        reject(chunksCount.error);
      };
    });
  } catch (error) {
    console.error('getDBStats error:', error);
    throw error;
  }
};

export default {
  initDB,
  saveChunk,
  getPendingChunks,
  getChunksByStatus,
  updateChunkStatus,
  deleteChunk,
  getUploadMetadata,
  updateUploadMetadata,
  getIncompleteUploads,
  cleanupCompletedMeeting,
  getDBStats
};
